import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Eye, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDespachos } from "@/hooks/useDespachos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ClientProcesses = ({ client }: { client: any }) => {
    const navigate = useNavigate();
    const { processes, updateProcess } = useDespachos();
    const [isLinkOpen, setIsLinkOpen] = useState(false);
    const [selectedProcessId, setSelectedProcessId] = useState("");

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // Filtrar los procesos de este cliente
    const clientProcesses = processes.filter(
        p => (p.client_name && p.client_name.trim().toLowerCase() === client.name?.trim().toLowerCase()) ||
             (p.metadata && p.metadata.client_id === client.id)
    );

    // Obtener los procesos que NO tienen cliente asignado
    const unlinkedProcesses = processes.filter(p => !p.client_name && p.metadata?.client_id !== client.id);

    const handleLinkProcess = async () => {
        if (!selectedProcessId) return;
        
        // Actualizar el proceso para asignarle el nombre del cliente y client_id
        await updateProcess(selectedProcessId, {
            client_name: client.name,
            client_id: client.id
        });
        
        setIsLinkOpen(false);
        setSelectedProcessId("");
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Procesos (Expedientes)</CardTitle>
                    <p className="text-xs text-gray-400">Expedientes judiciales asignados a {client.name}</p>
                </div>
                <Dialog open={isLinkOpen} onOpenChange={setIsLinkOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md">
                            <Plus className="h-4 w-4" /> Vincular Expediente
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-gray-900 font-bold">Vincular Expediente Existente</DialogTitle>
                            <p className="text-xs text-gray-400">Selecciona un proceso libre para asociarlo a este cliente</p>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            {unlinkedProcesses.length > 0 ? (
                                <Select value={selectedProcessId} onValueChange={setSelectedProcessId}>
                                    <SelectTrigger className="rounded-xl bg-white">
                                        <SelectValue placeholder="Selecciona un proceso..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {unlinkedProcesses.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>
                                                {p.case_number} - {p.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p className="text-xs text-gray-500 italic text-center py-4 bg-gray-50 rounded-xl">
                                    No hay expedientes libres sin cliente asignado.
                                </p>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsLinkOpen(false)}>Cancelar</Button>
                            <Button 
                                className="rounded-xl bg-legal-blue" 
                                onClick={handleLinkProcess}
                                disabled={!selectedProcessId}
                            >
                                Vincular
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                {clientProcesses.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="font-semibold text-gray-500">Radicado</TableHead>
                                <TableHead className="font-semibold text-gray-500">Título / Asunto</TableHead>
                                <TableHead className="font-semibold text-gray-500">Despacho</TableHead>
                                <TableHead className="font-semibold text-gray-500">Estado</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Acción</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {clientProcesses.map((p) => (
                                <TableRow key={p.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell className="font-bold text-gray-800 text-xs">{p.case_number}</TableCell>
                                    <TableCell className="font-semibold text-gray-700 text-xs truncate max-w-[200px]">{p.title}</TableCell>
                                    <TableCell className="text-gray-500 text-xs truncate max-w-[150px]">{p.juzgado || '—'}</TableCell>
                                    <TableCell>
                                        <Badge className={`text-[10px] font-bold py-0.5 px-2 rounded-full border ${
                                            p.status === 'active' 
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                                : 'bg-gray-50 text-gray-500 border-gray-200'
                                        }`}>
                                            {p.status === 'active' ? 'Vigilado' : 'Inactivo'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => navigate(`/dashboard/despachos/procesos/${p.id}`)}
                                            className="h-8 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-bold gap-1.5"
                                        >
                                            <Eye className="h-3.5 w-3.5" /> Ver Detalle
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <Link2 className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay procesos vinculados a este cliente.</p>
                        <p className="text-xs text-gray-300 mt-1">Usa el botón superior para asociar un expediente existente.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
